import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { servicesApi, type ServiceDto } from "@/api/endpoints";

interface Props {
  value: ServiceDto | null;
  onChange: (v: ServiceDto | null) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/** Lookup over active service masters, for the SERVICE line type on billing. */
export function ServicePicker({ value, onChange, label = "Service", required, error, helperText, disabled }: Props) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["services-picker", debounced],
    queryFn: () => servicesApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });
  return (
    <Autocomplete<ServiceDto>
      value={value}
      options={q.data?.items ?? []}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
